/**
 * NOVA-054 — RecallPage. The comptoir desk-recall worklist (D-24). When a corrected report is re-issued
 * (`reissueCompteRendu`) AND the prior version's desk copy was physically delivered, the new report's
 * comptoir channel is stamped `motif=rappel_reedition` — a marker meaning "a superseded copy is in the
 * patient's hands; recall it and hand over the corrected report". This screen lists those open recall
 * markers and lets the front desk acknowledge a recovered copy (mark-recovered → the corrected report
 * re-delivered, the marker closed). The acknowledge is audited server-side (a desk delivery of the
 * corrected report). PHI-safe: only the report numero/version + opaque refs are shown.
 *
 * Gated on `results.canal.recall` (module reach is results.compterendu.read; this surface adds a local
 * ForbiddenState + the server re-checks). The 5 states: loading/error/empty + offline + forbidden.
 */
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, EmptyState, ErrorState, ForbiddenState, LoadingState, OfflineState, Table, useToast, type Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import type { ApiError } from "@/api";
import { PageHeader } from "./components/PageHeader";
import { RESULTS_CAPS } from "./caps";
import { useRecallWorklist, useRecoverRecall } from "./hooks";
import type { RecallLine } from "./types";

export function RecallPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canRecall = useCan(RESULTS_CAPS.recall);

  const { data, isLoading, isError, error, refetch } = useRecallWorklist();
  const recover = useRecoverRecall();

  if (!online) return <OfflineState cached={false} />;
  if (!canRecall) return <ForbiddenState title={t("results.recall.title")} requiredCapabilities={[RESULTS_CAPS.recall]} />;
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => void refetch()} />;

  const rows = data ?? [];

  const onRecover = async (line: RecallLine) => {
    try {
      const out = await recover.mutateAsync({ canalId: line.canal_id });
      toast.success(out.idempotent_noop ? t("results.recall.alreadyRecovered") : t("results.recall.recovered"));
    } catch (e) {
      toast.error((e as ApiError)?.message ?? t("results.recall.error"));
    }
  };

  const columns: Column<RecallLine>[] = [
    { key: "numero", header: t("results.recall.col.numero"), mono: true, cell: (l) => <span className="nv-mono nv-ltr-island">{l.numero ?? "—"}</span> },
    { key: "version", header: t("results.recall.col.version"), mono: true, cell: (l) => <span className="nv-mono nv-ltr-island">v{l.version ?? "—"}</span> },
    {
      key: "delivered",
      header: t("results.recall.col.recalled"),
      mono: true,
      cell: (l) => <span className="nv-mono nv-ltr-island">v{l.delivered_version ?? "—"}</span>,
    },
    { key: "status", header: t("results.recall.col.status"), cell: () => <Badge tone="warn">{t("results.recall.pending")}</Badge> },
    {
      key: "open",
      header: "",
      cell: (l) =>
        l.compte_rendu_id ? (
          <Button size="sm" variant="ghost" onClick={() => navigate(`/resultats/comptes-rendus/${l.compte_rendu_id}`)}>
            {t("results.recall.openCr")}
          </Button>
        ) : null,
    },
    {
      key: "action",
      header: "",
      align: "end",
      cell: (l) => (
        <Button size="sm" onClick={() => void onRecover(l)} loading={recover.isPending && recover.variables?.canalId === l.canal_id}>
          {t("results.recall.recover")}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={t("results.recall.title")} subtitle={t("results.recall.subtitle")} />
      <Card title={t("results.recall.queue")} padded={rows.length === 0}>
        {rows.length === 0 ? (
          <EmptyState glyph="↺" title={t("results.recall.empty.title")} description={t("results.recall.empty.desc")} />
        ) : (
          <Table<RecallLine> columns={columns} rows={rows} rowKey={(l) => l.canal_id} />
        )}
      </Card>
    </div>
  );
}
